import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowLeft } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext';
import { updatePageMetadata, pageMetadata } from '../../utils/pageMetadata';
import { OnboardingCarousel, LANDING_PAGE_DATA } from '../onboarding';

/**
 * Responsive Image Component
 * Automatically generates @1x/@2x srcset for optimized WebP images
 */
const ResponsiveImage = ({ 
  src, 
  alt, 
  className 
}: { 
  src: string; 
  alt: string; 
  className: string;
}) => {
  // Generate @2x path by replacing @1x with @2x
  const src2x = src.replace('@1x', '@2x');
  
  return (
    <picture>
      <source 
        srcSet={`${src} 1x, ${src2x} 2x`}
        type="image/webp"
      />
      <img
        src={src}
        alt={alt}
        className={className}
      />
    </picture>
  );
};

export default function GetStarted() {
  const navigate = useNavigate();
  const { signInWithGoogle } = useAuth();
  
  // Update page metadata on mount
  useEffect(() => {
    updatePageMetadata(pageMetadata.home.title, pageMetadata.home.description);
  }, []);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(() => {
    // Check if user has seen onboarding before
    return localStorage.getItem('heysrilanka_onboarding_seen') !== 'true';
  });

  const handleGetStarted = () => {
    navigate('/app/chat');
  };

  const handleLogin = () => {
    setShowAuthModal(true);
  };

  const handleBackToOnboarding = () => {
    localStorage.removeItem('heysrilanka_onboarding_seen');
    setShowOnboarding(true);
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setGoogleError('');
      await signInWithGoogle(credentialResponse.credential);
      setShowAuthModal(false);
      navigate('/app/chat');
    } catch (error) {
      setGoogleError('Sign-in failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    setGoogleError('Google sign-in failed. Please try again.');
  };

  // Check if onboarding was completed and update state
  useEffect(() => {
    // Only run the interval if onboarding is still showing
    if (!showOnboarding) {
      return; // Don't poll if we're already on the static page
    }

    const checkOnboardingStatus = () => {
      const hasCompleted = localStorage.getItem('heysrilanka_onboarding_seen') === 'true';
      if (hasCompleted) {
        setShowOnboarding(false);
      }
    };

    // Listen for localStorage changes (in case user completes onboarding)
    window.addEventListener('storage', checkOnboardingStatus);
    
    // Also check periodically (for same-window updates) - only when carousel is showing
    const interval = setInterval(checkOnboardingStatus, 500);

    return () => {
      window.removeEventListener('storage', checkOnboardingStatus);
      clearInterval(interval);
    };
  }, [showOnboarding]);

  // Handle escape key and prevent body scroll when modal is open
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAuthModal) {
        setShowAuthModal(false);
      }
    };
    
    if (showAuthModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showAuthModal]);

  // Get the landing page (page 6) data
  const finalPage = LANDING_PAGE_DATA;

  return (
    <>
      {/* Show onboarding carousel for first-time users */}
      {showOnboarding && <OnboardingCarousel />}
      
      {/* Show Page 6 design as the landing page after onboarding is completed/skipped */}
      {!showOnboarding && (
        <div className="fixed inset-0 bg-white overflow-hidden">
          {/* Back Button - Top left (fixed to viewport like onboarding) */}
          <button
            onClick={handleBackToOnboarding}
            className="fixed top-5 left-5 md:top-24 md:left-20 z-20 w-[36px] h-[36px] border border-[#e5e7eb] rounded-[8px] flex items-center justify-center bg-white hover:bg-gray-50 transition-colors duration-200"
            aria-label="Go back to onboarding"
          >
            <ArrowLeft className="w-4 h-4 text-[#6b7280]" />
          </button>

          {/* Mobile Layout */}
          <div className="md:hidden h-screen-safe flex flex-col w-full">
            {/* Illustration Area */}
            <div className="relative h-[60%] overflow-hidden bg-[#fafafa]">
              {/* Main swing illustration */}
              <ResponsiveImage
                src={finalPage.images.mobile[0]}
                alt="Swing illustration"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            {/* Divider */}
            <div className="h-px bg-[#e5e7eb]" />

            {/* Content Area */}
            <div className="flex-1 px-8 pt-8 pb-8 flex flex-col justify-between">
              <div className="flex-1 flex flex-col items-center">
                <h1
                  className="text-[28px] font-bold leading-[33.6px] tracking-[-0.56px] text-[#1a1a1a] text-center mb-4"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {finalPage.title}
                </h1>
                <p
                  className="text-[16px] font-normal leading-[24px] text-[#6b7280] text-center max-w-[316px]"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {finalPage.description}
                </p>
              </div>

              {/* Buttons */}
              <div className="space-y-3 mt-8">
                <button
                  type="button"
                  onClick={handleGetStarted}
                  className="w-full h-[52px] bg-gradient-to-r from-[#14b8a6] to-[#663399] hover:from-[#0f9d8e] hover:to-[#552288] active:from-[#0d8a7a] active:to-[#4F2D32] text-white font-semibold text-[16px] leading-[24px] rounded-[12px] transition-colors duration-200"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    boxShadow: '0px 4px 12px 0px rgba(102, 51, 153, 0.25)'
                  }}
                >
                  Get Started
                </button>
                <button
                  type="button"
                  onClick={handleLogin}
                  className="w-full h-[52px] bg-white hover:bg-gray-50 text-gray-900 font-semibold text-[16px] leading-[24px] rounded-[12px] transition-colors duration-200 border-2 border-gray-200"
                  style={{
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  Log In
                </button>
                
                {/* Legal Links */}
                <div className="flex items-center justify-center gap-4 pt-4">
                  <a
                    href="/privacy"
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 underline"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Privacy Policy
                  </a>
                  <span className="text-gray-300">•</span>
                  <a
                    href="/terms"
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 underline"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Terms of Service
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout - Split Screen */}
          <div className="hidden md:flex h-screen-safe w-full relative">
            {/* Left Panel - Illustrations (719px) */}
            <div className="relative w-[719px] bg-[#fafafa] overflow-hidden">
              {/* Main illustration - swing scene */}
              <div className="absolute h-[909px] left-0 top-0 w-[719px]">
                <ResponsiveImage
                  src={finalPage.images.desktop[0]}
                  alt="Swing illustration"
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                />
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="w-px bg-[#e5e7eb]" />

            {/* Right Panel - Content */}
            <div className="flex-1 flex items-end justify-center pb-32 px-16 relative">
              <div className="max-w-[541px] text-center relative z-0">
                {/* Shell/conch decorative element - positioned relative to content, behind text */}
                <div className="absolute w-[118%] h-auto -top-40 left-1/2 -translate-x-1/2 pointer-events-none z-0">
                  <ResponsiveImage
                    src={finalPage.images.desktop[1]}
                    alt="Shell decoration"
                    className="w-full h-auto object-contain"
                  />
                </div>
                
                {/* Content with higher z-index to stay on top */}
                <div className="relative z-20">
                <h1
                  className="text-[32px] font-bold leading-[40px] tracking-[-0.56px] text-[#1a1a1a] mb-5"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {finalPage.title}
                </h1>
                <p
                  className="text-[20px] font-normal leading-[26px] text-[#6b7280] mb-10"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {finalPage.description}
                </p>

                {/* Buttons */}
                <div className="space-y-3 flex flex-col items-center">
                  <button
                    type="button"
                    onClick={handleGetStarted}
                    className="w-[327px] h-[52px] bg-gradient-to-r from-[#14b8a6] to-[#663399] hover:from-[#0f9d8e] hover:to-[#552288] active:from-[#0d8a7a] active:to-[#4F2D32] text-white font-semibold text-[16px] leading-[24px] rounded-[12px] transition-colors duration-200"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      boxShadow: '0px 4px 12px 0px rgba(102, 51, 153, 0.25)'
                    }}
                  >
                    Get Started
                  </button>
                  <button
                    type="button"
                    onClick={handleLogin}
                    className="w-[327px] h-[52px] bg-white hover:bg-gray-50 text-gray-900 font-semibold text-[16px] leading-[24px] rounded-[12px] transition-colors duration-200 border-2 border-gray-200"
                    style={{
                      fontFamily: 'Inter, sans-serif'
                    }}
                  >
                    Log In
                  </button>
                
                {/* Legal Links */}
                <div className="flex items-center justify-center gap-4 pt-4">
                  <a
                    href="/privacy"
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 underline"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Privacy Policy
                  </a>
                  <span className="text-gray-300">•</span>
                  <a
                    href="/terms"
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 underline"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Terms of Service
                  </a>
                </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Hide old GetStarted page content */}
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 hidden">
        <div className="max-w-md w-full text-center">
          {/* Logo/Icon */}
          <div className="mb-8">
            <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🏝️</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Hey Sri Lanka</h1>
            <p className="text-gray-600">Your personal travel companion</p>
          </div>

          {/* Features */}
          <div className="mb-8 space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <span className="text-2xl">🗺️</span>
              <span className="text-gray-700">Discover amazing places</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <span className="text-2xl">🍽️</span>
              <span className="text-gray-700">Find the best restaurants</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <span className="text-2xl">🏨</span>
              <span className="text-gray-700">Book perfect accommodations</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <span className="text-2xl">💬</span>
              <span className="text-gray-700">Get instant travel advice</span>
            </div>
          </div>

          {/* Get Started Button */}
          <button
            onClick={handleGetStarted}
            className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl mb-3"
          >
            Get Started
          </button>

          {/* Login Button */}
          <button
            onClick={handleLogin}
            className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg border border-gray-200"
          >
            Log In
          </button>

          {/* Footer */}
          <p className="text-sm text-gray-500 mt-6">
            Join thousands of travelers exploring Sri Lanka
          </p>
        </div>
      </div>

      {/* Auth Modal/Bottom Sheet */}
      {showAuthModal && (
        <>
          {/* Desktop Modal */}
          <div 
            className="hidden md:fixed md:inset-0 md:flex md:items-center md:justify-center md:z-[100] md:bg-black md:bg-opacity-40 animate-fade-in"
            onClick={() => setShowAuthModal(false)}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl w-[420px] relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => setShowAuthModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 transition-colors"
              >
                <X size={20} />
              </button>
              <div className="p-8">
                {/* Compact Auth Content */}
                <div className="text-center space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome to Hey Sri Lanka</h2>
                    <p className="text-gray-600 text-sm">Sign in with Google to continue</p>
                  </div>

                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      text="continue_with"
                      size="large"
                      width="320"
                      logo_alignment="left"
                    />
                  </div>

                  {googleError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-600 text-sm">{googleError}</p>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="relative flex items-center">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="flex-shrink mx-4 text-gray-500 text-sm">OR</span>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>

                  {/* Email/Password Auth Button */}
                  <button
                    onClick={() => {
                      setShowAuthModal(false);
                      navigate('/email-auth');
                    }}
                    className="w-full py-3 px-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Continue with Email
                  </button>

                  <div className="text-xs text-gray-500 space-y-2">
                    <p>
                      By continuing, you agree to Hey Sri Lanka's{' '}
                      <a href="/terms" className="text-gray-600 hover:text-gray-900 underline transition-colors">
                        Terms of Service
                      </a>
                    </p>
                    <div className="flex items-center justify-center gap-3 text-[11px]">
                      <a
                        href="/privacy"
                        className="text-gray-600 hover:text-gray-900 underline transition-colors"
                      >
                        Privacy Policy
                      </a>
                      <span className="text-gray-300">•</span>
                      <a
                        href="/terms"
                        className="text-gray-600 hover:text-gray-900 underline transition-colors"
                      >
                        Terms of Service
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Bottom Sheet */}
          <div className="md:hidden fixed inset-x-0 bottom-0 z-[100] bg-white rounded-t-2xl shadow-lg animate-slide-up">
            <div className="flex justify-end p-4 border-b border-gray-200">
              <button 
                onClick={() => setShowAuthModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 pb-8">
              {/* Compact Auth Content */}
              <div className="text-center space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">Welcome to Hey Sri Lanka</h2>
                  <p className="text-gray-600 text-sm">Sign in with Google to continue</p>
                </div>

                <div className="flex justify-center">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="continue_with"
                    size="large"
                    width="280"
                    logo_alignment="left"
                  />
                </div>

                {googleError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-600 text-sm">{googleError}</p>
                  </div>
                )}

                {/* Divider */}
                <div className="relative flex items-center">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="flex-shrink mx-4 text-gray-500 text-sm">OR</span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>

                {/* Email/Password Auth Button */}
                <button
                  onClick={() => {
                    setShowAuthModal(false);
                    navigate('/email-auth');
                  }}
                  className="w-full py-3 px-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Continue with Email
                </button>

                <div className="text-xs text-gray-500 space-y-2">
                  <p>
                    By continuing, you agree to Hey Sri Lanka's{' '}
                    <a href="/terms" className="text-gray-600 hover:text-gray-900 underline transition-colors">
                      Terms of Service
                    </a>
                  </p>
                  <div className="flex items-center justify-center gap-3 text-[11px]">
                    <a
                      href="/privacy"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Privacy Policy
                    </a>
                    <span className="text-gray-300">•</span>
                    <a
                      href="/terms"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Terms of Service
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
